import Image from 'next/image';
import useTranslation from 'shared/hooks/useTranslation';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import useIsInViewport from 'shared/hooks/useIsInViewport';
import { scrollBar } from 'shared/components/mixins';
import { DEVICE } from 'config/theme';
import { Wrapper } from './styled';

function JoinTrustMateCarousel(): JSX.Element {
  const { t } = useTranslation('ourTeamPage');
  const gridRef = useRef<HTMLDivElement>(null);

  const threshold = { threshold: 0 };
  const isVideoInView = useIsInViewport(gridRef, threshold);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const { isMatch: isDesktop, mediaLoaded } = useMediaQuery(DEVICE.SM);

  // eslint-disable-next-line consistent-return
  function startScrolling() {
    if (mediaLoaded && isDesktop) {
      const gridElement = gridRef.current;
      if (gridElement && !intervalId) {
        let { scrollLeft } = gridRef.current;
        let scrollDirection = 1;
        const scrollInterval = setInterval(() => {
          scrollLeft += 1 * scrollDirection;
          gridElement.scrollLeft = scrollLeft;
          if (scrollLeft >= gridElement.scrollWidth - gridElement.clientWidth) {
            scrollDirection = -1;
          } else if (scrollLeft <= 0) {
            scrollDirection = 1;
          }
        }, 50);
        setIntervalId(scrollInterval);

        return () => {
          if (intervalId) {
            clearInterval(intervalId);
          }
        };
      }
    }
  }

  // eslint-disable-next-line consistent-return
  function stopScrolling() {
    const gridElement = gridRef.current;
    if (gridElement && intervalId) {
      setIntervalId(null);
      return clearInterval(intervalId);
    }
  }

  useEffect(() => {
    startScrolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoInView, isDesktop]);

  return (
    <Wrapper maxWidth>
      <HeaderH2>
        {t('joinTrustMate')}
        <br />
        <span>{t('andBuild')}</span>
      </HeaderH2>
      <GridWrapper
        onMouseDown={() => stopScrolling()}
        onMouseEnter={() => stopScrolling()}
        onMouseLeave={() => startScrolling()}
        ref={gridRef}
      >
        <Grid>
          <Card>
            <OrangeText>10,000+</OrangeText>
            <WhiteText>{t('happyCustomers')}</WhiteText>
          </Card>
          <Card>
            <Image src="/img/our-team/podcast.png" alt="" layout="fill" objectFit="cover" quality={100} />
          </Card>
          <Card>
            <Image src="/img/our-team/team.png" alt="" layout="fill" objectFit="cover" quality={100} />
          </Card>
          <Card>
            <Image src="/img/our-team/nb.png" alt="" layout="fill" objectFit="cover" quality={100} />
          </Card>
          <Card>
            <OrangeText>35</OrangeText>
            <WhiteText>{t('trustMateExperts')}</WhiteText>
          </Card>
          <Card>
            <Image src="/img/our-team/small-team.png" alt="" layout="fill" objectFit="cover" quality={100} />
          </Card>
          <Card>
            <Image src="/img/our-team/kubota.png" alt="" layout="fill" objectFit="cover" quality={100} />
          </Card>
          <Card>
            <Image src="/img/our-team/hotels.png" alt="" layout="fill" objectFit="cover" quality={100} />
          </Card>
        </Grid>
      </GridWrapper>
    </Wrapper>
  );
}

const HeaderH2 = styled.h2`
  color: #fff;
  text-align: center;
  font-size: 3.2rem;
  line-height: 106%;
  font-weight: 600;
  letter-spacing: 1.109px;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 5.4rem;
    line-height: 111%;
  }

  @media (max-width: ${({ theme }) => theme.BREAKPOINT.MD}) {
    span {
      color: #fff;
      font-size: 2rem;
      line-height: 3.4rem;
    }
  }
`;

const GridWrapper = styled.div`
  max-width: 100%;
  overflow-x: scroll;
  ${scrollBar};

  @media ${({ theme }) => theme.DEVICE.MD} {
    scrollbar-width: none;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 1.2rem;
  justify-content: start;
  margin-top: 2.4rem;
  height: 26.5rem;
  grid-template:
    'a c c d f g' 1fr
    'b c c e f h' 1fr / 1fr 1fr;
  grid-template-columns: repeat(6, 19rem);
  grid-template-rows: repeat(2, 12.6rem);
  width: fit-content;
  overflow-x: auto;

  @media ${({ theme }) => theme.DEVICE.MD} {
    gap: 2.2rem;
    height: 46.5rem;
    grid-template-columns: repeat(6, 33rem);
    grid-template-rows: repeat(2, 22.1rem);
  }

  > div:nth-of-type(1) {
    grid-area: a;
  }

  > div:nth-of-type(2) {
    grid-area: b;
  }

  > div:nth-of-type(3) {
    grid-area: c;
  }

  > div:nth-of-type(4) {
    grid-area: d;
  }

  > div:nth-of-type(5) {
    grid-area: e;
  }

  > div:nth-of-type(6) {
    grid-area: f;
  }

  > div:nth-of-type(7) {
    grid-area: g;
  }

  > div:nth-of-type(8) {
    grid-area: h;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: ${({ theme }) => theme.COLORS.V2.BACKGROUND};
  border-radius: 0.8rem;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const OrangeText = styled.div`
  color: ${({ theme }) => theme.COLORS.SECONDARY};
  font-size: 2.3rem;
  line-height: 124%;
  letter-spacing: 0.06rem;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 4.1rem;
  }
`;

const WhiteText = styled.div`
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 128%;

  @media ${({ theme }) => theme.DEVICE.MD} {
    font-size: 1.3rem;
  }
`;

export default JoinTrustMateCarousel;
